import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Orb } from "@/components/earnos/Orb";
import { Button, Input, useToast } from "@/components/earnos/kit";
import { apiFetch } from "@/lib/api";


export default function Auth() {
  const { login, register, loginWithGoogle, authError, user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  useEffect(() => {
    if (!authLoading && user) navigate(user.onboarded ? "/home" : "/onboarding", { replace: true });
  }, [user, authLoading, navigate]);
  const toast = useToast();
  const [mode, setMode] = useState("signup");
  const [firstName, setFirstName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [resetToken, setResetToken] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const m = window.location.href.match(/[?#&]reset_token=([^&#]+)/);
    if (m) {
      setResetToken(decodeURIComponent(m[1]));
      setMode("reset");
    }
  }, []);

  const submit = async () => {
    setLoading(true);
    try {
      if (mode === "signup") {
        if (!firstName.trim()) throw new Error("Please tell EARNOS your name");
        if (password.length < 6) throw new Error("Password must be at least 6 characters");
        await register(email.trim(), password, firstName.trim());
        navigate("/onboarding", { replace: true });
        return;
      } else if (mode === "signin") {
        const u = await login(email.trim(), password);
        navigate(u.onboarded ? "/home" : "/onboarding", { replace: true });
      } else {
        if (!resetToken) {
          await apiFetch("/auth/reset/request", { method: "POST", auth: false, body: { email: email.trim() } });
          toast.show("If an account exists, a reset link has been sent to your email.", "success");
          setMode("signin");
        } else {
          if (password.length < 6) throw new Error("Password must be at least 6 characters");
          await apiFetch("/auth/reset/confirm", { method: "POST", auth: false, body: { token: resetToken, new_password: password } });
          toast.show("Password updated. Please sign in.", "success");
          setMode("signin");
          setResetToken("");
          setPassword("");
        }
      }
    } catch (e) {
      toast.show(e?.message || "Something went wrong", "error");
    } finally {
      setLoading(false);
    }
  };

  const cta = mode === "signup" ? "Build My Income Company  →" : mode === "signin" ? "Continue" : resetToken ? "Update Password" : "Send Reset Token";

  return (
    <div className="min-h-screen flex items-center justify-center px-6 py-12" style={{ background: "radial-gradient(ellipse at top, #1A1030, #0A0A0C 55%)" }}>
      <div className="w-full max-w-md">
        <div className="flex flex-col items-center mb-8">
          <Orb state={loading ? "thinking" : "idle"} size={110} />
          <h1 className="text-4xl font-extrabold tracking-[0.15em] text-ink mt-4">EARNOS</h1>
          <span className="mt-2 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-widest text-brand-light bg-brand/15 border border-brand/30">AI Income Company</span>
          <p className="text-base font-semibold text-ink mt-4 text-center">AI That Builds, Runs &amp; Grows Your Income</p>
          <p className="text-sm text-ink-2 mt-1 text-center">Build your personalized income engine in minutes.</p>
        </div>

        <div>
          {mode === "signup" && (
            <Input label="What should EARNOS call you?" placeholder="Your first name" value={firstName} onChange={(e) => setFirstName(e.target.value)} testID="auth-firstname-input" />
          )}
          <Input label="Your email" placeholder="you@example.com" type="email" value={email} onChange={(e) => setEmail(e.target.value)} testID="auth-email-input" />
          {(mode !== "reset" || resetToken) && (
            <Input label={mode === "reset" ? "New password" : "Create a secure password"} placeholder="••••••••" type="password" value={password} onChange={(e) => setPassword(e.target.value)} testID="auth-password-input" />
          )}

          {authError && <p role="alert" className="text-sm text-amber-300 mb-4">{authError}</p>}
          <Button title={cta} onClick={submit} loading={loading} full testID="auth-submit-button" />

          {mode === "signup" && <p className="text-sm text-ink-2 text-center mt-3">Your AI income journey starts here. Review the <Link className="text-brand-light underline" to="/legal/terms">Terms draft</Link>, <Link className="text-brand-light underline" to="/legal/privacy">Privacy draft</Link>, and <Link className="text-brand-light underline" to="/legal/earnings">AI &amp; earnings disclosure</Link> before signing up.</p>}

          {mode !== "reset" && (
            <>
              <div className="flex items-center my-5">
                <div className="flex-1 h-px bg-line" />
                <span className="mx-3 text-sm text-ink-2">or</span>
                <div className="flex-1 h-px bg-line" />
              </div>
              <Button title="Continue with Google" variant="secondary" full onClick={loginWithGoogle} testID="auth-google-button" />
            </>
          )}

          <div className="flex flex-col items-center mt-6 gap-2">
            {mode === "signup" && (
              <button onClick={() => setMode("signin")} data-testid="auth-switch-signin" className="text-base text-ink-2">
                Already have an account? <span className="text-brand-light font-bold">Sign in</span>
              </button>
            )}
            {mode === "signin" && (
              <>
                <button onClick={() => setMode("signup")} data-testid="auth-switch-signup" className="text-base text-ink-2">
                  New here? <span className="text-brand-light font-bold">Create account</span>
                </button>
                <button onClick={() => setMode("reset")} data-testid="auth-forgot" className="text-base text-ink-2">Forgot password?</button>
              </>
            )}
            {mode === "reset" && (
              <button onClick={() => { setMode("signin"); setResetToken(""); }} data-testid="auth-back-signin" className="text-base text-ink-2">
                Back to <span className="text-brand-light font-bold">Sign in</span>
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
